



























































































































































































































































































































































import {
  defineComponent,
  PropType,
  watch,
  ref,
  computed,
  onMounted,
} from '@vue/composition-api';
import {
  AppliedProperty,
  ItemPropertyRow,
  LinkItem,
  PreselectionProperty,
  PropertyValue,
  PropertyVariable,
  TableReferenceType,
} from '@oare/types';
import PropertyValueDisplay from './PropertyValueDisplay.vue';
import sl from '@/serviceLocator';
import * as munsell from 'munsell';
import PropertyInfo from './PropertyInfo.vue';

export default defineComponent({
  name: 'PropertyVariable',
  props: {
    variable: {
      type: Object as PropType<PropertyVariable>,
      required: true,
    },
    showUuid: {
      type: Boolean,
      default: false,
    },
    nodesToHighlight: {
      type: Array as PropType<String[]>,
      default: [],
    },
    openSearchResults: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    overrideCustom: {
      type: Boolean,
      default: false,
    },
    showValidation: {
      type: Boolean,
      default: true,
    },
    existingProperties: {
      type: Array as PropType<ItemPropertyRow[]>,
      required: false,
    },
    valuesToPreselect: {
      type: Array as PropType<PreselectionProperty[]>,
      required: false,
    },
  },
  components: {
    PropertyValueDisplay,
    PropertyInfo,
  },
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const _ = sl.get('lodash');

    const copyUuid = (uuid: string) => {
      navigator.clipboard.writeText(uuid);
      actions.showSnackbar('Copied Successfully');
    };

    const openPanels = ref<number>();

    watch(
      () => props.openSearchResults,
      () => {
        if (props.openSearchResults) {
          props.variable.values.forEach((value, idx) => {
            if (
              props.nodesToHighlight.includes(value.hierarchy.uuid) &&
              value.variables.length > 0
            ) {
              openPanels.value = idx;
            }
          });
        } else {
          openPanels.value = undefined;
        }
      },
      { immediate: true }
    );

    const appliedProperties = ref<AppliedProperty[]>([]);
    watch(appliedProperties, () => {
      emit(
        'update-properties',
        appliedProperties.value.map(p => ({
          ...p,
          sourceUuid: props.variable.hierarchy.uuid,
        }))
      );
    });
    const updateProperties = (
      childProperties: AppliedProperty[],
      value: PropertyValue
    ) => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== value.hierarchy.uuid
      );
      appliedProperties.value.push(...childProperties);
    };

    // Determines which values to display below the variable. Essentially just hides additional values when the variable is custom (1) and has a value already selected
    const valuesToDisplay = computed(() => {
      const relevantProperties = appliedProperties.value.filter(
        p =>
          p.variableRow.hierarchy.uuid === props.variable.hierarchy.uuid &&
          p.valueRow
      );
      if (
        props.variable.hierarchy.custom === 1 &&
        relevantProperties.length > 0 &&
        !props.overrideCustom
      ) {
        const valueHierarchyUuids = relevantProperties.map(
          p => p.valueRow!.hierarchy.uuid
        );
        return props.variable.values.filter(val =>
          valueHierarchyUuids.includes(val.hierarchy.uuid)
        );
      }
      return props.variable.values;
    });
    watch(valuesToDisplay, () => {
      if (
        valuesToDisplay.value.length === 1 &&
        props.variable.hierarchy.custom === 1 &&
        !props.overrideCustom
      ) {
        openPanels.value = 0;
      }
    });

    const setUpAutoOpen = () => {
      if (props.variable.hierarchy.custom === 1 && !props.overrideCustom) {
        emit('auto-open-next');
      }
    };

    // Generates blue text displayed on closed panels to indicate what has been selected for that variable. Only applies to nominal and munsell variables
    const selectionDisplay = computed(() => {
      if (
        props.variable.type !== 'nominal' &&
        props.variable.type !== 'munsell'
      ) {
        return '';
      }
      const relevantProperties = appliedProperties.value.filter(
        p => p.variableRow.hierarchy.uuid === props.variable.hierarchy.uuid
      );
      if (relevantProperties.length === 0) {
        return '';
      }
      return relevantProperties
        .map(p => {
          if (p.variableRow.type === 'nominal' && p.valueRow) {
            return p.valueRow.name;
          }
          if (p.variableRow.type === 'munsell' && p.value) {
            return munsell.munsellToHex(p.value as string);
          }
          return '';
        })
        .join(', ');
    });

    const integralOrdinalInput = ref<string>();
    watch(integralOrdinalInput, () => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== props.variable.hierarchy.uuid
      );
      if (
        integralOrdinalInput.value &&
        integralOrdinalRule(integralOrdinalInput.value, true)
      ) {
        appliedProperties.value.push({
          variableRow: props.variable,
          valueRow: null,
          value: integralOrdinalInput.value,
          objectUuid: null,
          objectDisplay: null,
          sourceUuid: props.variable.hierarchy.uuid,
        });
      }
    });
    const integralOrdinalRule = (input: string, errorAsBoolean = false) => {
      if (!input) {
        return true;
      }
      const isInteger = _.isInteger(Number(input));
      if (!isInteger) {
        return errorAsBoolean ? false : 'Must be an integer';
      }
      return true;
    };

    const applyLogical = ref(false);
    const logicalInput = ref<boolean>();
    watch(applyLogical, () => {
      if (applyLogical.value) {
        logicalInput.value = true;
      } else {
        logicalInput.value = undefined;
      }
    });
    watch(logicalInput, () => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== props.variable.hierarchy.uuid
      );
      if (logicalInput.value !== undefined) {
        appliedProperties.value.push({
          variableRow: props.variable,
          valueRow: null,
          value: logicalInput.value,
          objectUuid: null,
          objectDisplay: null,
          sourceUuid: props.variable.hierarchy.uuid,
        });
      }
    });

    const decimalInput = ref<string>();
    watch(decimalInput, () => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== props.variable.hierarchy.uuid
      );
      if (decimalInput.value && decimalRule(decimalInput.value, true)) {
        appliedProperties.value.push({
          variableRow: props.variable,
          valueRow: null,
          value: decimalInput.value,
          objectUuid: null,
          objectDisplay: null,
          sourceUuid: props.variable.hierarchy.uuid,
        });
      }
    });
    const decimalRule = (input: string, errorAsBoolean = false) => {
      if (!input) {
        return true;
      }
      const numberInput = Number(input);
      if (Number.isNaN(numberInput)) {
        return errorAsBoolean ? false : 'Must be a number';
      }
      return true;
    };

    const alphanumericInput = ref<string>();
    watch(alphanumericInput, () => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== props.variable.hierarchy.uuid
      );
      if (alphanumericInput.value) {
        appliedProperties.value.push({
          variableRow: props.variable,
          valueRow: null,
          value: alphanumericInput.value,
          objectUuid: null,
          objectDisplay: null,
          sourceUuid: props.variable.hierarchy.uuid,
        });
      }
    });

    const munsellInput = ref<string>();
    watch(munsellInput, () => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== props.variable.hierarchy.uuid
      );
      if (munsellInput.value && munsellRule(munsellInput.value, true)) {
        appliedProperties.value.push({
          variableRow: props.variable,
          valueRow: null,
          value: munsellInput.value,
          objectUuid: null,
          objectDisplay: null,
          sourceUuid: props.variable.hierarchy.uuid,
        });
      }
    });
    const munsellRule = (input: string, errorAsBoolean = false) => {
      if (!input) {
        return true;
      }
      try {
        munsell.munsellToRgb(input);
      } catch (e) {
        return errorAsBoolean ? false : 'Must be a valid Munsell color';
      }
      return true;
    };

    const calendricalYearInput = ref<string>();
    const calendricalMonthInput = ref<string>();
    const calendricalDayInput = ref<string>();
    watch(
      [calendricalYearInput, calendricalMonthInput, calendricalDayInput],
      () => {
        appliedProperties.value = appliedProperties.value.filter(
          p => p.sourceUuid !== props.variable.hierarchy.uuid
        );
        if (
          calendricalYearInput.value &&
          calendricalYearRule(calendricalYearInput.value, true)
        ) {
          let displayValue = `${calendricalYearInput.value}`;
          if (
            calendricalMonthInput.value &&
            calendricalMonthRule(calendricalMonthInput.value, true)
          ) {
            displayValue += `-${calendricalMonthInput.value}`;
            if (
              calendricalDayInput.value &&
              calendricalDayRule(calendricalDayInput.value, true)
            ) {
              displayValue += `-${calendricalDayInput.value}`;
            }
          }

          appliedProperties.value.push({
            variableRow: props.variable,
            valueRow: null,
            value: displayValue,
            objectUuid: null,
            objectDisplay: null,
            sourceUuid: props.variable.hierarchy.uuid,
          });
        }
      }
    );
    const calendricalYearRule = (input: string, errorAsBoolean = false) => {
      if (!input) {
        if (calendricalMonthInput.value || calendricalDayInput.value) {
          return errorAsBoolean
            ? false
            : 'A valid year is required when a month is specified';
        }
        return true;
      }
      const isInteger = _.isInteger(Number(input));
      if (!isInteger || input.length !== 4) {
        return errorAsBoolean ? false : 'Must be a valid year (YYYY)';
      }
      return true;
    };
    const calendricalMonthRule = (input: string, errorAsBoolean = false) => {
      if (!input) {
        if (calendricalDayInput.value) {
          return errorAsBoolean
            ? false
            : 'A valid month is required when a day is specified';
        }
        return true;
      }
      const inputAsNumber = Number(input);
      const isInteger = _.isInteger(inputAsNumber);
      if (
        !isInteger ||
        inputAsNumber < 1 ||
        inputAsNumber > 12 ||
        input.length !== 2
      ) {
        return errorAsBoolean ? false : 'Must be a valid month (MM)';
      }
      return true;
    };
    const calendricalDayRule = (input: string, errorAsBoolean = false) => {
      if (!input) {
        return true;
      }
      const inputAsNumber = Number(input);
      const isInteger = _.isInteger(inputAsNumber);
      if (
        !isInteger ||
        inputAsNumber < 1 ||
        inputAsNumber > 31 ||
        input.length !== 2
      ) {
        return errorAsBoolean ? false : 'Must be a valid day (DD)';
      }
      return true;
    };

    const linkInput = ref<LinkItem>();
    const linkLoading = ref(false);
    const linkSearch = ref<string>();
    const linkItems = ref<LinkItem[]>([]);

    const linkRequiresTextFilter = computed(() => {
      return props.variable.tableReference === 'text_discourse';
    });
    const linkTextFilterInput = ref<LinkItem>();
    const linkTextFilterLoading = ref(false);
    const linkTextFilterSearch = ref<string>();
    const linkTextFilterItems = ref<LinkItem[]>([]);
    watch(
      linkTextFilterSearch,
      _.debounce(async () => {
        try {
          linkTextFilterLoading.value = true;
          if (!linkTextFilterSearch.value) {
            linkTextFilterItems.value = [];
            linkTextFilterInput.value = undefined;
            return;
          }
          if (linkTextFilterInput.value) {
            return;
          }
          linkTextFilterItems.value = await server.searchLinkProperties(
            linkTextFilterSearch.value,
            'text'
          );
        } catch (err) {
          actions.showErrorSnackbar(
            'Error loading link options. Please try again.',
            err as Error
          );
        } finally {
          linkTextFilterLoading.value = false;
        }
      }, 500)
    );

    const linkLabel = (tableReference: TableReferenceType | null) => {
      if (!tableReference) {
        return 'Link Unavailable';
      }
      return tableReference.split(/[_?]/g).map(_.capitalize).join(' ');
    };
    watch(
      linkSearch,
      _.debounce(async () => {
        try {
          linkLoading.value = true;
          if (!linkSearch.value || !props.variable.tableReference) {
            linkItems.value = [];
            linkInput.value = undefined;
            return;
          }
          if (linkInput.value) {
            return;
          }
          linkItems.value = await server.searchLinkProperties(
            linkSearch.value,
            props.variable.tableReference,
            linkTextFilterInput.value
              ? linkTextFilterInput.value.objectUuid
              : undefined
          );
        } catch (err) {
          actions.showErrorSnackbar(
            'Error loading link options. Please try again.',
            err as Error
          );
        } finally {
          linkLoading.value = false;
        }
      }, 500)
    );
    watch(linkInput, () => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== props.variable.hierarchy.uuid
      );
      if (linkInput.value) {
        appliedProperties.value.push({
          variableRow: props.variable,
          valueRow: null,
          value: null,
          objectUuid: linkInput.value.objectUuid,
          objectDisplay: linkTextFilterInput.value
            ? `${linkTextFilterInput.value.objectDisplay} - ${linkInput.value.objectDisplay}`
            : linkInput.value.objectDisplay,
          sourceUuid: props.variable.hierarchy.uuid,
        });
      }
    });

    // Determines whether or not a green check should be displayed, indicating that the variable and any subtrees are complete
    const showCheck = computed(() => {
      if (ignored.value) {
        return true;
      }

      const numSubtrees = props.variable.values.filter(
        val => val.variables.length > 0
      ).length;

      if (numSubtrees === 0) {
        const variablePropertyIsApplied =
          appliedProperties.value.filter(
            p => p.variableRow.hierarchy.uuid === props.variable.hierarchy.uuid
          ).length > 0;
        return variablePropertyIsApplied;
      } else if (
        props.variable.hierarchy.custom === 1 &&
        !props.overrideCustom
      ) {
        const variableProperties = appliedProperties.value.filter(
          p => p.variableRow.hierarchy.uuid === props.variable.hierarchy.uuid
        );
        if (variableProperties.length === 1) {
          const variableProperty = variableProperties[0];
          if (variableProperty.valueRow!.variables.length === 0) {
            return true;
          } else {
            return completedSubtrees.value.includes(
              variableProperty.valueRow!.hierarchy.uuid
            );
          }
        } else {
          return false;
        }
      }
      return numSubtrees === completedSubtrees.value.length;
    });
    const ignored = ref(false);

    const completedSubtrees = ref<string[]>([]);

    watch(showCheck, isComplete => {
      emit('completed', isComplete);
    });
    watch(ignored, isIgnored => {
      emit('ignored', isIgnored);
    });

    const handleCompleted = (uuid: string, isCompleted: boolean) => {
      if (isCompleted) {
        completedSubtrees.value.push(uuid);
      } else {
        completedSubtrees.value = completedSubtrees.value.filter(
          u => u !== uuid
        );
      }
    };
    const handleIgnored = (idx: number, isIgnored: boolean) => {
      if (isIgnored && idx === openPanels.value) {
        openPanels.value = undefined;
      }
    };

    onMounted(async () => {
      // If existing properties are supplied, they will be automatically selected or applied on mount
      if (props.existingProperties) {
        const relevantProperties = props.existingProperties.filter(p => {
          const parentProperty = props.existingProperties!.find(
            exis => exis.uuid === p.parentUuid
          );
          const hasValidParentRelationship = parentProperty
            ? props.variable.hierarchy.objectParentUuid ===
              parentProperty.valueUuid
            : true;

          const hasValidGrandparentRelationship = parentProperty
            ? props.variable.hierarchy.objectGrandparentUuid ===
              parentProperty.variableUuid
            : true;
          return (
            props.variable.type !== 'nominal' &&
            p.level === props.variable.level &&
            p.variableUuid === props.variable.uuid &&
            hasValidParentRelationship &&
            hasValidGrandparentRelationship
          );
        });
        if (relevantProperties.length !== 1) {
          return;
        }
        const relevantProperty = relevantProperties[0];

        if (
          props.variable.type === 'integral' ||
          props.variable.type === 'ordinal'
        ) {
          integralOrdinalInput.value = relevantProperty.value;
        } else if (props.variable.type === 'logical') {
          applyLogical.value = true;
          logicalInput.value = !!relevantProperty.value;
        } else if (props.variable.type === 'decimal') {
          decimalInput.value = relevantProperty.value;
        } else if (props.variable.type === 'alphanumeric') {
          alphanumericInput.value = relevantProperty.value;
        } else if (props.variable.type === 'munsell') {
          munsellInput.value = relevantProperty.value;
        } else if (props.variable.type === 'calendrical') {
          const splitValue = relevantProperty.value.split('-');
          calendricalYearInput.value = splitValue[0];
          if (splitValue.length > 1) {
            calendricalMonthInput.value = splitValue[1];
          }
          if (splitValue.length > 2) {
            calendricalDayInput.value = splitValue[2];
          }
        } else if (
          props.variable.type === 'link' &&
          props.variable.tableReference
        ) {
          linkSearch.value = relevantProperty.objectUuid;
          linkItems.value = await server.searchLinkProperties(
            linkSearch.value,
            props.variable.tableReference
          );
          linkInput.value = {
            objectUuid: relevantProperty.objectUuid,
            objectDisplay: linkItems.value[0].objectDisplay,
          };
        }
      }
    });

    return {
      copyUuid,
      openPanels,
      appliedProperties,
      updateProperties,
      setUpAutoOpen,
      selectionDisplay,
      integralOrdinalRule,
      integralOrdinalInput,
      applyLogical,
      logicalInput,
      decimalInput,
      decimalRule,
      alphanumericInput,
      munsellInput,
      munsellRule,
      calendricalYearInput,
      calendricalMonthInput,
      calendricalDayInput,
      calendricalYearRule,
      calendricalMonthRule,
      calendricalDayRule,
      showCheck,
      completedSubtrees,
      ignored,
      handleCompleted,
      linkInput,
      linkLoading,
      linkSearch,
      linkItems,
      linkLabel,
      valuesToDisplay,
      handleIgnored,
      linkRequiresTextFilter,
      linkTextFilterInput,
      linkTextFilterLoading,
      linkTextFilterItems,
      linkTextFilterSearch,
    };
  },
});
